/**
 * Registro de depuración en memoria. En el iPad no hay inspector de Safari desde Windows,
 * así que la app guarda lo que pasa por la consola y lo muestra en pantalla (§9.5).
 */

export type LogLevel = 'log' | 'info' | 'warn' | 'error'

export interface LogEntry {
  id: number
  at: number
  level: LogLevel
  message: string
}

const MAX_ENTRIES = 500
const entries: LogEntry[] = []
const listeners = new Set<() => void>()
let nextId = 1
let installed = false

function stringify(value: unknown): string {
  if (typeof value === 'string') return value
  if (value instanceof Error)
    return `${value.name}: ${value.message}${value.stack ? `\n${value.stack}` : ''}`
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}

export function pushLog(level: LogLevel, parts: unknown[]): void {
  entries.push({ id: nextId++, at: Date.now(), level, message: parts.map(stringify).join(' ') })
  if (entries.length > MAX_ENTRIES) entries.splice(0, entries.length - MAX_ENTRIES)
  listeners.forEach((listener) => listener())
}

export function getLogEntries(): readonly LogEntry[] {
  return entries
}

export function subscribeLog(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function clearLog(): void {
  entries.length = 0
  listeners.forEach((listener) => listener())
}

export function installDebugLog(): void {
  if (installed) return
  installed = true
  for (const message of window.__atrilBootErrors ?? []) pushLog('error', [message])
  const levels: LogLevel[] = ['log', 'info', 'warn', 'error']
  for (const level of levels) {
    const original = console[level].bind(console)
    console[level] = (...parts: unknown[]) => {
      pushLog(level, parts)
      original(...parts)
    }
  }
  window.addEventListener('error', (event) => {
    pushLog('error', [`${event.message} @ ${event.filename}:${event.lineno}`])
  })
  window.addEventListener('unhandledrejection', (event) => {
    pushLog('error', ['Promesa rechazada:', event.reason])
  })
}
